import React, { useState, useEffect } from "react";
import { Calendar, MapPin, Clock, Star, Eye, Heart, Share2, Download, CalendarPlus, Filter, Search, Moon, Sun, Grid, List, TrendingUp, X, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import PageTemplate from "../components/Common/PageTemplate";
import DatePicker from "react-datepicker";
import { FacebookShareButton, TwitterShareButton, WhatsappShareButton, LinkedinShareButton, FacebookIcon, TwitterIcon, WhatsappIcon, LinkedinIcon } from "react-share";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import "react-datepicker/dist/react-datepicker.css";
import EventCard from "../components/EventCard";
import EventModal from "../components/EventModal";
import etkinliklerData from "../data/Etkinlikler.json";

const Etkinlikler = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState("all");
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedType, setSelectedType] = useState("all");
    const [viewMode, setViewMode] = useState("grid");
    const [currentPage, setCurrentPage] = useState(1);
    const [eventsPerPage] = useState(9);
    const [sortOrder, setSortOrder] = useState("upcoming");
    
    // Modal states
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showModal, setShowModal] = useState(false);
    
    // Advanced filters
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [favorites, setFavorites] = useState(
        JSON.parse(localStorage.getItem("eventFavorites")) || []
    );
    const [darkMode, setDarkMode] = useState(
        JSON.parse(localStorage.getItem("darkMode")) || false
    );
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

    useEffect(() => {
        fetchEvents();
    }, []);

    useEffect(() => {
        localStorage.setItem("eventFavorites", JSON.stringify(favorites));
    }, [favorites]);

    useEffect(() => {
        localStorage.setItem("darkMode", JSON.stringify(darkMode));
        if (darkMode) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }, [darkMode]);

    const fetchEvents = async () => {
        setLoading(true);
        try {
            // Local JSON dosyasından veri yükle ve field isimlerini map et
            const mappedEvents = etkinliklerData.map((event, index) => ({
                id: index + 1,
                title: event.baslik,
                event_date: event.tarih,
                location: event.konum,
                event_type: event.category,
                image_url: event.imageURL,
                description: event.aciklama || '',
                is_active: true
            }));
            setEvents(mappedEvents);
        } catch (err) {
            setError("Etkinlikler yüklenirken bir hata oluştu.");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const formatEventDate = (dateString) => {
        if (!dateString) return { date: '', time: '' };
        const date = new Date(dateString);
        return {
            date: date.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" }),
            time: date.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })
        };
    };

    const isUpcoming = (dateString) => new Date(dateString) > new Date();

    const getEventTypeColor = (type) => ({
        'konferans': 'bg-blue-100 text-blue-800',
        'seminer': 'bg-green-100 text-green-800',
        'workshop': 'bg-purple-100 text-purple-800',
        'senlik': 'bg-orange-100 text-orange-800',
        'toren': 'bg-red-100 text-red-800',
        'diger': 'bg-gray-100 text-gray-800'
    }[type] || 'bg-gray-100 text-gray-800');

    const getEventTypeText = (type) => ({
        'konferans': 'Konferans',
        'seminer': 'Seminer',
        'workshop': 'Workshop',
        'senlik': 'Şenlik',
        'toren': 'Tören',
        'diger': 'Diğer'
    }[type] || 'Diğer');

    const toggleFavorite = (eventId) => {
        setFavorites(prev =>
            prev.includes(eventId) ? prev.filter(id => id !== eventId) : [...prev, eventId]
        );
    };

    const handleEventClick = (event) => {
        // Fotoğrafı direkt aç
        if (event.image_url) {
            window.open(event.image_url, '_blank');
        }
    };

    const addToCalendar = (event) => {
        const startDate = new Date(event.event_date);
        const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000);
        const icsContent = `BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:-//Hacettepe Fen Fakültesi//Etkinlikler//TR\nBEGIN:VEVENT\nUID:${event.id}@hacettepe.edu.tr\nDTSTAMP:${new Date().toISOString().replace(/[-:.]/g, '')}Z\nDTSTART:${startDate.toISOString().replace(/[-:.]/g, '')}Z\nDTEND:${endDate.toISOString().replace(/[-:.]/g, '')}Z\nSUMMARY:${event.title}\nDESCRIPTION:${event.description || ''}\nLOCATION:${event.location || ''}\nEND:VEVENT\nEND:VCALENDAR`;
        const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${event.title}.ics`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const filteredAndSortedEvents = events
        .filter(event => {
            if (filter === 'upcoming' && !isUpcoming(event.event_date)) return false;
            if (filter === 'past' && isUpcoming(event.event_date)) return false;
            if (filter === 'favorites' && !favorites.includes(event.id)) return false;
            if (selectedCategories.length > 0 && !selectedCategories.includes(event.event_type)) return false;
            const eventDate = new Date(event.event_date);
            if (startDate && eventDate < startDate) return false;
            if (endDate && eventDate > endDate) return false;
            const searchLower = searchTerm.toLowerCase();
            if (searchTerm && !(
                event.title.toLowerCase().includes(searchLower) ||
                event.location.toLowerCase().includes(searchLower) ||
                (event.description && event.description.toLowerCase().includes(searchLower))
            )) return false;
            return true;
        })
        .sort((a, b) => {
            const dateA = new Date(a.event_date);
            const dateB = new Date(b.event_date);
            const now = new Date();
            if (sortOrder === 'upcoming') {
                const aIsUpcoming = dateA > now;
                const bIsUpcoming = dateB > now;
                if (aIsUpcoming && !bIsUpcoming) return -1;
                if (!aIsUpcoming && bIsUpcoming) return 1;
                if (aIsUpcoming && bIsUpcoming) return dateA - dateB;
                return dateB - dateA;
            }
            return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
        });

    const indexOfLastEvent = currentPage * eventsPerPage;
    const currentEvents = filteredAndSortedEvents.slice(indexOfLastEvent - eventsPerPage, indexOfLastEvent);
    const totalPages = Math.ceil(filteredAndSortedEvents.length / eventsPerPage);
    const eventTypes = [...new Set(events.map(event => event.event_type))];

    useEffect(() => { setCurrentPage(1); }, [searchTerm, filter, sortOrder, selectedCategories, startDate, endDate]);

    if (loading) return <PageTemplate title="Etkinlikler"><div className="text-center p-8">Yükleniyor...</div></PageTemplate>;
    if (error) return <PageTemplate title="Etkinlikler"><div className="text-center p-8 text-red-500">{error}</div></PageTemplate>;

    return (
        <div className={darkMode ? "dark" : ""}>
            <PageTemplate title="Etkinlikler" subtitle="Fen Fakültesi etkinliklerini keşfedin">
                {/* Theme Toggle & View Controls */}
                <motion.div 
                    className="flex justify-between items-center mb-6"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => setDarkMode(!darkMode)}
                            className={`p-3 rounded-xl transition-all ${darkMode ? 'bg-gray-700 text-yellow-400' : 'bg-gray-100 text-gray-600'} hover:scale-110`}
                        >
                            {darkMode ? <Sun size={20} /> : <Moon size={20} />}
                        </button>
                        
                        <div className="flex bg-gray-100 dark:bg-gray-700 rounded-xl p-1">
                            {['grid', 'list'].map(mode => (
                                <button
                                    key={mode}
                                    onClick={() => setViewMode(mode)}
                                    className={`p-2 rounded-lg transition-all ${viewMode === mode ? 'bg-white dark:bg-gray-600 shadow-sm' : 'hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                                >
                                    {mode === 'grid' && <Grid size={16} />}
                                    {mode === 'list' && <List size={16} />}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                        <TrendingUp size={16} className="text-blue-500" />
                        <span>
                            {filteredAndSortedEvents.length} etkinlik bulundu
                        </span>
                    </div>
                </motion.div>

                {/* Enhanced Header Stats */}
                <motion.div 
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 rounded-xl text-white">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-blue-100 text-sm">Toplam Etkinlik</p>
                                <p className="text-2xl font-bold">{events.length}</p>
                            </div>
                            <Calendar className="text-blue-200" size={32} />
                        </div>
                    </div>
                    <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 rounded-xl text-white">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-green-100 text-sm">Yaklaşan</p>
                                <p className="text-2xl font-bold">{events.filter(e => isUpcoming(e.event_date)).length}</p>
                            </div>
                            <Clock className="text-green-200" size={32} />
                        </div>
                    </div>
                    <div className="bg-gradient-to-r from-red-500 to-red-600 p-6 rounded-xl text-white">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-red-100 text-sm">Favorilerim</p>
                                <p className="text-2xl font-bold">{favorites.length}</p>
                            </div>
                            <Heart className="text-red-200" size={32} />
                        </div>
                    </div>
                    <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-6 rounded-xl text-white">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-purple-100 text-sm">Etkinlik Türü</p>
                                <p className="text-2xl font-bold">{eventTypes.length}</p>
                            </div>
                            <Star className="text-purple-200" size={32} />
                        </div>
                    </div>
                    <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-6 rounded-xl text-white">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-orange-100 text-sm">Bu Sayfada</p>
                                <p className="text-2xl font-bold">{filteredAndSortedEvents.length}</p>
                            </div>
                            <Eye className="text-orange-200" size={32} />
                        </div>
                    </div>
                </motion.div>

                {/* Search and Filters */}
                <motion.div 
                    className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 mb-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Filtrele ve Ara</h3>
                        <button
                            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-all text-sm"
                        >
                            <Filter size={16} />
                            {showAdvancedFilters ? 'Filtreyi Gizle' : 'Gelişmiş Filtre'}
                        </button>
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                        <div className="lg:col-span-12">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                                <input
                                    type="text"
                                    placeholder="Etkinlik ara (başlık, konum, açıklama)..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Advanced Filters */}
                    <AnimatePresence>
                        {showAdvancedFilters && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700"
                            >
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Başlangıç Tarihi</label>
                                        <DatePicker
                                            selected={startDate}
                                            onChange={date => setStartDate(date)}
                                            selectsStart
                                            startDate={startDate}
                                            endDate={endDate}
                                            placeholderText="Başlangıç tarihi"
                                            className="w-full py-3 px-4 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                            dateFormat="dd/MM/yyyy"
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Bitiş Tarihi</label>
                                        <DatePicker
                                            selected={endDate}
                                            onChange={date => setEndDate(date)}
                                            selectsEnd
                                            startDate={startDate}
                                            endDate={endDate}
                                            minDate={startDate}
                                            placeholderText="Bitiş tarihi"
                                            className="w-full py-3 px-4 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                            dateFormat="dd/MM/yyyy"
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Durum</label>
                                        <select
                                            value={filter}
                                            onChange={(e) => setFilter(e.target.value)}
                                            className="w-full py-3 px-4 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                        >
                                            <option value="all">Tümü</option>
                                            <option value="upcoming">Yaklaşan</option>
                                            <option value="past">Geçmiş</option>
                                            <option value="favorites">Favoriler</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Sırala</label>
                                        <select
                                            value={sortOrder}
                                            onChange={(e) => setSortOrder(e.target.value)}
                                            className="w-full py-3 px-4 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                        >
                                            <option value="upcoming">Yaklaşana Göre</option>
                                            <option value="newest">En Yeni</option>
                                            <option value="oldest">En Eski</option>
                                        </select>
                                    </div>
                                    
                                    <div className="lg:col-span-4">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Kategoriler</label>
                                        <div className="flex flex-wrap gap-2">
                                            {eventTypes.map(type => (
                                                <button
                                                    key={type}
                                                    onClick={() => {
                                                        const newCategories = selectedCategories.includes(type)
                                                            ? selectedCategories.filter(c => c !== type)
                                                            : [...selectedCategories, type];
                                                        setSelectedCategories(newCategories);
                                                    }}
                                                    className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                                                        selectedCategories.includes(type)
                                                            ? 'bg-blue-500 text-white'
                                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                                                    }`}
                                                >
                                                    {getEventTypeText(type)}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>

                {/* Events Grid/List */}
                <AnimatePresence>
                    {currentEvents.length === 0 ? (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }} 
                            className="text-center py-16"
                        >
                            <Calendar size={64} className="mx-auto text-gray-300 mb-6" />
                            <h3 className="text-2xl font-semibold text-gray-600 dark:text-gray-300 mb-3">
                                Sonuç bulunamadı
                            </h3>
                            <p className="text-gray-500 dark:text-gray-400 text-lg">
                                Farklı filtreleme seçeneklerini deneyin.
                            </p>
                        </motion.div>
                    ) : (
                        <motion.div 
                            className={`grid gap-8 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}
                            variants={{
                                hidden: { opacity: 0 },
                                visible: {
                                    opacity: 1,
                                    transition: { staggerChildren: 0.05 }
                                }
                            }}
                            initial="hidden"
                            animate="visible"
                        >
                            {currentEvents.map((event) => (
                                <EventCard
                                    key={event.id}
                                    event={event}
                                    onCardClick={handleEventClick}
                                    onToggleFavorite={toggleFavorite}
                                    isFavorite={favorites.includes(event.id)}
                                    viewMode={viewMode}
                                />
                            ))}
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Pagination */}
                {totalPages > 1 && (
                    <motion.div 
                        className="flex justify-center items-center mt-12 space-x-2"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5 }}
                    >
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                            Önceki
                        </button>
                        
                        {[...Array(totalPages)].map((_, index) => (
                            <button
                                key={index + 1}
                                onClick={() => setCurrentPage(index + 1)}
                                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                                    currentPage === index + 1
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                            >
                                {index + 1}
                            </button>
                        ))}
                        
                        <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                            Sonraki
                        </button>
                    </motion.div>
                )}
                
                {/* Event Modal */}
                <AnimatePresence>
                    {selectedEvent && (
                        <EventModal 
                            event={selectedEvent} 
                            onClose={() => setSelectedEvent(null)}
                            isFavorite={favorites.includes(selectedEvent.id)}
                            onToggleFavorite={toggleFavorite}
                            onAddToCalendar={addToCalendar}
                        />
                    )}
                </AnimatePresence>
            </PageTemplate>
        </div>
    );
};

export default Etkinlikler; 